import { graphql, Link } from 'gatsby';
import { IoMdPlay } from 'react-icons/io';
// import getYoutubeID from 'get-youtube-id';
// import { ModalContext } from './ModalContext';
import { BiLinkExternal } from 'react-icons/bi';
import type { PostEntryProps } from '../types';
import SocialShare from './SocialShare';
import PostMeta from './PostMeta';
import PostFeaturedImage from './PostFeaturedImage';

function LinkWrapper({
  children,
  post,
  // isVideo,
  linkTarget,
  className,
  tabIndex,
}: {
  children: React.ReactNode;
  post: PostEntryProps;
  // isVideo?: boolean;
  linkTarget: string;
  className?: string;
  tabIndex?: number;
}) {
  // const { setModal } = useContext(ModalContext);

  // const handleVideoLinkClick = (e: React.MouseEvent) => {
  //   if (!isVideo) {
  //     return;
  //   }

  //   e.preventDefault();
  //   setModal({
  //     modalOpen: true,
  //     modalContent: {
  //       youtube: getYoutubeID(post.uri),
  //     },
  //   });
  // };

  if (linkTarget === '_blank') {
    return (
      <a
        href={post.uri}
        className={className}
        target={linkTarget}
        // onClick={handleVideoLinkClick}
        tabIndex={tabIndex}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={post.uri}
      target={linkTarget}
      className={className}
      tabIndex={tabIndex}
    >
      {children}
    </Link>
  );
}

export default function PostEntry({
  post,
  image,
  excerpt,
  social,
  meta,
}: {
  post: PostEntryProps;
  image?: boolean;
  excerpt?: boolean;
  social?: boolean;
  meta?: boolean;
}) {
  const isVideo =
    post.contentType?.node?.name === 'story' || post.postDetails?.videoUrl;
  const linkTarget =
    post.uri.startsWith('http') && !isVideo ? '_blank' : '_self';

  return (
    <article className="post-entry">
      <div className="post-entry__inner">
        {image && (
          <LinkWrapper
            // isVideo={isVideo}
            linkTarget={linkTarget}
            post={post}
            className="post-entry__link--img post-entry__link--overlay"
            tabIndex={-1}
          >
            <div className="post-entry__img">
              {isVideo && (
                <div className="post-entry__icon">
                  <IoMdPlay />
                </div>
              )}
              <div className="post-entry__img__gradient gradient" />
              {post.image || <PostFeaturedImage post={post} fallback />}
            </div>
          </LinkWrapper>
        )}
        <div className="post-entry__details">
          <h3 className="post-entry__title h5 narrow">
            <LinkWrapper
              // isVideo={isVideo}
              linkTarget={linkTarget}
              post={post}
              className="post-entry__link--title"
            >
              <span style={{ whiteSpace: 'nowrap' }}>
                <span
                  style={{ whiteSpace: 'initial' }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: post.title }}
                />
                &nbsp;
                {linkTarget === '_blank' && !isVideo && <BiLinkExternal />}
              </span>
            </LinkWrapper>
          </h3>
          {excerpt && post.excerpt && (
            <div
              className="post-entry__excerpt narrow"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
          )}
          {meta && (
            <PostMeta
              date={post.date}
              categories={post.categories?.nodes}
              externalSite={{
                siteTitle: post.postDetails?.siteTitle,
                favicon: post.remoteFavicon,
              }}
            />
          )}
        </div>
      </div>
      {social && (
        <div className="post-entry__social">
          <SocialShare title={post.title} absoluteUrl={post.uri} />
        </div>
      )}
    </article>
  );
}
export function PostEntrySkeleton({
  image,
  excerpt,
  social,
  meta,
}: {
  image?: boolean;
  excerpt?: boolean;
  social?: boolean;
  meta?: boolean;
}) {
  return (
    <article className="post-entry">
      <div className="post-entry__inner">
        {image && (
          <div className="post-entry__img">
            <div
              className="post-thumbnail skeleton-bg"
              style={{ width: '100%', paddingBottom: '56.25%' }}
            />
          </div>
        )}
        <div className="post-entry__details">
          <h3
            className="post-entry__title h5 narrow skeleton-bg"
            style={{ width: '100%' }}
          >
            &nbsp;
          </h3>
          {excerpt && (
            <div
              className="post-entry__excerpt narrow skeleton-bg"
              style={{ width: '80%' }}
            >
              &nbsp;
            </div>
          )}
          {meta && (
            <div
              className="post-entry__meta small skeleton-bg"
              style={{ width: '50%' }}
            >
              &nbsp;
            </div>
          )}
        </div>
      </div>
      {social && (
        <div
          className="post-entry__social skeleton-bg"
          style={{ width: '50%' }}
        >
          &nbsp;
        </div>
      )}
    </article>
  );
}

export const query = graphql`
  fragment PostEntry on WpPost {
    id
    title
    date
    # contentTypeName
    contentType {
      node {
        name
      }
    }
    categories {
      nodes {
        uri
        slug
        name
      }
    }
    uri
    excerpt
    featuredImage {
      node {
        localFile {
          childImageSharp {
            gatsbyImageData(aspectRatio: 1.7778, layout: FULL_WIDTH)
          }
        }
      }
    }
    youtubeImage {
      childImageSharp {
        gatsbyImageData(aspectRatio: 1.7778, layout: FULL_WIDTH)
      }
    }
    postDetails {
      siteTitle
      videoUrl
    }
    remoteFavicon {
      publicURL
      childImageSharp {
        gatsbyImageData(width: 32, height: 32)
      }
    }
  }

  fragment StoryEntry on WpStory {
    id
    title
    date
    uri
    # contentTypeName
    contentType {
      node {
        name
      }
    }
    excerpt
    youtubeImage {
      childImageSharp {
        gatsbyImageData(aspectRatio: 1.7778, layout: FULL_WIDTH)
      }
    }
    storyDetails {
      videoUrl
    }
  }
`;
